import { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { AnalystStepEnd } from '@/components/profile/AnalystStepEnd';
import { Button } from '@/components/ui/Button';
import { Skeleton, SkeletonGroup } from '@/components/ui/Skeleton';
import { Textarea } from '@/components/ui/Textarea';
import { AnalystMessageView } from '@/components/profile/AnalystMessageView';
import { ChatMarkdownRenderer } from '@/components/chat/ChatMarkdownRenderer';
import { THEME_LABELS } from '@/lib/company-qualification';
import { loadAnalystConversation, runAnalystTurn, stopAnalystTurn } from '@/lib/company-analyst';
import type { AnalystMessage, AnalystReadRequest, AnalystReading, AnalystScope } from '@shared/types/company-analyst';
import type { CompanyQualificationSheet, QualificationThemeKey } from '@shared/types/company-qualification';

interface Props {
  scope: AnalystScope;
  /** Unsaved edits in the sheet: the analyst waits until they are saved. */
  sheetDirty: boolean;
  onSheet: (sheet: CompanyQualificationSheet, version: number, closed: boolean) => void;
  onRunning: (running: boolean) => void;
  onClose: () => void;
  /** The scope is closed (broad pass done, theme deepened): the chat ends on where to go next. */
  closed: boolean;
  onShowAnalysis: () => void;
  onDeepen: (theme: QualificationThemeKey) => void;
  /** Opened from « Commencer l’analyse »: the first turn starts without a second click. */
  autoStart?: boolean;
}

interface LiveTurn { text: string; pending: AnalystReadRequest[]; readings: AnalystReading[] }
const EMPTY_TURN: LiveTurn = { text: '', pending: [], readings: [] };

/** What the analyst does while the person waits for its message: said once, before the text arrives. */
const waitingNote = (first: boolean, scope: AnalystScope, title: string): string => {
  if (!first) return 'L’analyste reprend votre réponse dans la fiche, vérifie ce qu’elle change, puis vous pose la question suivante. Comptez une minute environ.';
  return scope === 'broad'
    ? 'L’analyste lit votre site, votre CRM et vos documents, puis rédige ce qu’il a compris. Comptez une à deux minutes : sa première question arrive ensuite.'
    : `L’analyste relit la fiche et ce qu’il sait déjà sur « ${title} », puis vous pose sa première question. Comptez une minute environ.`;
};

/** The analyst's conversation for one scope: the broad qualification or one theme. */
export const AnalystChat = ({ scope, sheetDirty, onSheet, onRunning, onClose, closed, onShowAnalysis, onDeepen, autoStart = false }: Props) => {
  const [messages, setMessages] = useState<AnalystMessage[] | null>(null);
  const [running, setRunning] = useState(false);
  const [live, setLive] = useState<LiveTurn>(EMPTY_TURN);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const started = useRef(false);
  const title = scope === 'broad' ? 'Qualification' : THEME_LABELS[scope].label;

  const reload = useCallback(async () => {
    try {
      const loaded = await loadAnalystConversation(scope);
      setMessages(loaded.messages);
      setRunning(loaded.turnRunning);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Conversation indisponible.');
    }
  }, [scope]);

  useEffect(() => { setMessages(null); setLive(EMPTY_TURN); void reload(); }, [reload]);
  useEffect(() => { onRunning(running); }, [running, onRunning]);
  useEffect(() => { bottom.current?.scrollIntoView({ block: 'end' }); }, [messages, live.text]);

  const send = async (text: string | null) => {
    setError(null);
    setRunning(true);
    setLive(EMPTY_TURN);
    if (text) setMessages((current) => [...(current ?? []), { id: `local-${Date.now()}`, role: 'user', content: text, createdAt: new Date().toISOString() }]);
    setDraft('');
    try {
      const outcome = await runAnalystTurn(scope, text, {
        onPhase: () => setLive((turn) => ({ ...turn, text: '' })),
        onDelta: (delta) => setLive((turn) => ({ ...turn, text: turn.text + delta })),
        onReadings: ({ pending, readings }) => setLive((turn) => ({ ...turn, pending: pending ?? turn.pending, readings: readings ?? turn.readings })),
      });
      setMessages(outcome.messages);
      onSheet(outcome.sheet, outcome.version, outcome.closed);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'La réponse n’a pas abouti.');
      await reload();
    } finally {
      setRunning(false);
      setLive(EMPTY_TURN);
    }
  };

  const stop = async () => {
    try { await stopAnalystTurn(scope); } catch (cause) { setError(cause instanceof Error ? cause.message : 'Arrêt impossible.'); }
  };

  const canSend = !running && !sheetDirty;
  useEffect(() => {
    if (!autoStart || started.current || messages?.length !== 0 || running || sheetDirty) return;
    started.current = true;
    void send(null);
    // send is recreated each render; the ref runs the first turn once.
  }, [autoStart, messages, running, sheetDirty]);
  const firstTurn = !messages?.some((message) => message.role === 'analyst');
  const last = messages?.at(-1);
  return (
    <aside className="flex h-full min-h-[60vh] flex-col overflow-hidden rounded-xl border border-domino-cardBorder bg-domino-warm shadow-[var(--domino-shadow-soft)]">
      <header className="flex items-center justify-between border-b border-domino-cardBorder px-4 py-3">
        <p className="font-semibold text-domino-ink">{title}</p>
        <button type="button" aria-label="Fermer l’analyste" onClick={onClose} className="rounded p-1 text-domino-inkSoft hover:bg-domino-muted"><X className="h-4 w-4" /></button>
      </header>
      <div className="flex-1 space-y-4 overflow-y-auto bg-domino-ivory/60 px-4 py-4">
        {messages === null && (
          <SkeletonGroup label="Chargement de la conversation" className="space-y-3">
            <Skeleton className="h-24 w-full rounded-2xl rounded-tl-md" />
            <Skeleton className="ml-auto h-10 w-2/3 rounded-2xl rounded-br-md" />
            <Skeleton className="h-16 w-full rounded-2xl rounded-tl-md" />
          </SkeletonGroup>
        )}
        {messages?.length === 0 && !running && !autoStart && (
          <div className="space-y-3 text-sm text-domino-inkSoft">
            <p>
              {scope === 'broad'
                ? 'L’analyste lit votre site, votre CRM et vos documents, remplit la fiche, puis vous pose une question à la fois.'
                : `L’analyste approfondit « ${title} » : ce qu’il lit et ce que vous répondez arrive dans la même fiche.`}
            </p>
            <Button onClick={() => void send(null)} disabled={!canSend}>{scope === 'broad' ? 'Commencer' : 'Approfondir'}</Button>
          </div>
        )}
        {messages?.map((message) => <AnalystMessageView key={message.id} message={message} />)}
        {closed && !running && last?.role === 'analyst' && !last.failed && (
          <AnalystStepEnd scope={scope} deepen={last.deepen ?? []} onShowAnalysis={onShowAnalysis} onDeepen={onDeepen} />
        )}
        {running && (
          <div className="space-y-2 text-sm">
            {live.pending.length > 0 && (
              <p className="text-xs text-domino-inkSoft">
                {live.readings.length ? `Lu : ${live.readings.filter((reading) => reading.ok).length} sur ${live.readings.length}` : `Lecture de ${live.pending.length} source${live.pending.length > 1 ? 's' : ''}…`}
              </p>
            )}
            <div className="rounded-2xl rounded-tl-md border border-domino-cardBorder bg-white/70 px-4 py-3">
              {live.text
                ? <ChatMarkdownRenderer content={live.text} className="text-sm leading-6 text-domino-ink [&_strong]:text-domino-aubergine" />
                : <p className="text-sm leading-6 text-domino-inkSoft">{waitingNote(firstTurn, scope, title)}</p>}
            </div>
          </div>
        )}
        <div ref={bottom} />
      </div>
      <footer className="space-y-2 border-t border-domino-cardBorder p-3">
        {error && <p className="text-sm text-red-700">{error}</p>}
        {sheetDirty && <p className="text-xs text-amber-800">Enregistrez vos modifications de la fiche avant de reprendre avec l’analyste.</p>}
        <form className="flex gap-2" onSubmit={(event) => { event.preventDefault(); if (draft.trim()) void send(draft.trim()); }}>
          <Textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault();
                if (draft.trim() && canSend) void send(draft.trim());
              }
            }}
            rows={2}
            placeholder={closed ? 'Une correction ? (Entrée pour envoyer)' : 'Votre réponse… (Entrée pour envoyer)'}
            className="text-sm"
            disabled={running}
          />
          {running
            ? <Button type="button" variant="secondary" onClick={() => void stop()}>Arrêter</Button>
            : <Button type="submit" disabled={!canSend || !draft.trim()}>Envoyer</Button>}
        </form>
      </footer>
    </aside>
  );
};
